import React from 'react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';

const CartItemDetails = ({ item, formatPrice }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // Helper function to render a detail row
  const DetailRow = ({ label, value }) => {
    if (!value) return null;
    return (
      <div className="flex justify-between py-1.5">
        <span className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>{label}</span>
        <span className={`text-xs font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'}`}>{value}</span>
      </div>
    );
  };

  // Check if there's any additional info to show
  const hasAdditionalInfo = item.matnr || item.vendormat || item.matgroup || item.manufacturer ||
    item.manufacturer_part_number || item.storage_location || item.wbs_element ||
    item.network_number || item.asset_number || item.longtext || item.note ||
    item.einkauefergruppe || item.einkaueferorganisation;

  if (!hasAdditionalInfo) {
    return (
      <p className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'} italic`}>
        {t('cart.noFurtherDetails')}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Material & Artikel Info */}
      {(item.matnr || item.vendormat || item.matgroup) && (
        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-gray-800/30 border border-white/10' : 'bg-gray-50 border border-gray-200/50'}`}>
          <h4 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>
            {t('cart.detailsMaterial')}
          </h4>
          <div className="divide-y divide-white/5">
            <DetailRow label={t('cart.materialNumber')} value={item.matnr} />
            <DetailRow label={t('cart.supplierArtNo')} value={item.vendormat} />
            <DetailRow label={t('cart.productGroup')} value={item.matgroup} />
          </div>
        </div>
      )}

      {/* Hersteller Info */}
      {(item.manufacturer || item.manufacturer_part_number) && (
        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-gray-800/30 border border-white/10' : 'bg-gray-50 border border-gray-200/50'}`}>
          <h4 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>
            {t('cart.detailsManufacturer')}
          </h4>
          <div className="divide-y divide-white/5">
            <DetailRow label={t('cart.manufacturer')} value={item.manufacturer} />
            <DetailRow label={t('cart.manufacturerPartNo')} value={item.manufacturer_part_number} />
          </div>
        </div>
      )}

      {/* Kontierung Details */}
      {(item.storage_location || item.wbs_element || item.network_number || item.asset_number) && (
        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-gray-800/30 border border-white/10' : 'bg-gray-50 border border-gray-200/50'}`}>
          <h4 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>
            {t('cart.detailsAccounting')}
          </h4>
          <div className="divide-y divide-white/5">
            <DetailRow label={t('cart.storageLocation')} value={item.storage_location} />
            <DetailRow label={t('cart.wbsElement')} value={item.wbs_element} />
            <DetailRow label={t('cart.networkNumber')} value={item.network_number} />
            <DetailRow label={t('cart.assetNumber')} value={item.asset_number} />
          </div>
        </div>
      )}

      {/* Einkauf Details */}
      {(item.einkauefergruppe || item.einkaueferorganisation) && (
        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-gray-800/30 border border-white/10' : 'bg-gray-50 border border-gray-200/50'}`}>
          <h4 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>
            {t('cart.detailsPurchasing')}
          </h4>
          <div className="divide-y divide-white/5">
            <DetailRow label={t('cart.purchasingGroup')} value={item.einkauefergruppe} />
            <DetailRow label={t('cart.purchasingOrg')} value={item.einkaueferorganisation} />
          </div>
        </div>
      )}

      {/* Langtext / Notiz */}
      {(item.longtext || item.note) && (
        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-blue-900/20 border border-blue-400/20' : 'bg-blue-50 border border-blue-200/50'}`}>
          <h4 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-blue-300/70' : 'text-blue-600'}`}>
            {t('cart.detailsNotes')}
          </h4>
          {item.longtext && (
            <p className={`text-sm ${isDarkMode ? 'text-white/80' : 'text-gray-700'} whitespace-pre-wrap`}>
              {item.longtext}
            </p>
          )}
          {item.note && (
            <p className={`text-sm ${isDarkMode ? 'text-white/80' : 'text-gray-700'} whitespace-pre-wrap mt-2`}>
              {item.note}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CartItemDetails;
